// "Pause all / Resume all / Revoke all" on the company page — Phase 3b, M4 (ADR-0012).
//
// Every change runs through the `changeCompanyAccess` callable: the same shared path the single
// pause / revoke buttons and `/uma pause | resume | revoke` use. This panel previews first with
// `dry_run` (live, nothing written), lists every product the change reaches, and — for pause and
// revoke — asks for the company's name typed before the button enables, the same rule as Slack
// (ADR-0009 decision 4). Resume gives access back, so it takes a button.
//
// The click sends back the preview's fingerprint (`expected_digest`). If the company changed in
// between — a colleague granted another product, say — the backend refuses with nothing written
// and returns the live preview, which replaces the old one here: the same promise Slack's button
// keeps (ADR-0009 decision 5). The name has to be typed again for the new preview.
//
// Unlike Slack, the web handles RLS: RLS products are included, and their RLS rows are removed
// or restored server-side after their Users change.

// NPM Dependencies
import React, { Component } from "react";

// Local Dependencies
import CustomInput from "../misc/CustomInput";
import LoadingCircle from "../misc/Loading";

const TITLE = { pause: "Pause", resume: "Resume", revoke: "Revoke" };
const WORKING = { pause: "Pausing", resume: "Resuming", revoke: "Revoking" };

/** Shown while a change runs. A large company can take a minute. */
export const changeInProgress = (action) => [`${WORKING[action]}… a large company can take a minute.`, "secondary", 120000];

/** The call itself failed (network, timeout): how far the backend got is unknown, so say so. */
export const changeOutcomeUnknown = (action, err) => [
  `The ${action} failed and its outcome is unknown: ${(err && err.message) || err}. Check the access list and logs before retrying.`,
  "warning",
  20000,
];

const normalizeName = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Did the Employee type the company's name? Case and spacing are forgiven; nothing else is.
 * The same rule as `typedNameMatches` in functions/src/slack/change-views.ts.
 */
export const typedNameMatches = (typed, companyName) => {
  const want = normalizeName(companyName);
  return !!want && normalizeName(typed) === want;
};

/**
 * How a `changeCompanyAccess` answer is told to the Employee: `[message, alert type, ms]`.
 *
 * Never claims more than the backend said. "Queued" is not politeness — the Shopify tag writes
 * ride the 1-minute job runner — and a partial failure says so, because it is the one outcome
 * where doing nothing further is the wrong instinct.
 */
export const changeOutcome = (result) => {
  if (!result) return ["No answer came back, so the outcome is unknown. Check the logs before retrying.", "warning", 15000];
  if (result.status === "queued") return [result.message, "primary", 8000];
  if (result.partially_applied) return [result.message, "danger", 30000];
  return [result.message || "The change was refused. Nothing was changed.", "warning", 15000];
};

class BulkAccessChange extends Component {
  state = {
    action: null, // "pause" | "resume" | "revoke" while the panel is open
    preview: null, // the dry_run answer
    typed: "",
    busy: false,
    changedSince: false, // the last click was refused because the company changed after the preview
  };

  open = async (action) => {
    this.setState({ action, preview: null, typed: "", busy: true, changedSince: false });
    try {
      const response = await this.props.shopify.changeCompanyAccess({
        company_id: this.props.companyId,
        action,
        target: "all",
        dry_run: true,
      });
      this.setState({ preview: response.data, busy: false });
    } catch (err) {
      console.error(err);
      this.setState({ preview: { status: "error", message: `The preview failed: ${err.message || err}` }, busy: false });
    }
  };

  close = () => this.setState({ action: null, preview: null, typed: "", busy: false, changedSince: false });

  confirm = async () => {
    const { action, preview } = this.state;
    this.setState({ busy: true });
    this.props.setAlert(...changeInProgress(action));
    try {
      const response = await this.props.shopify.changeCompanyAccess({
        company_id: this.props.companyId,
        action,
        target: "all",
        expected_digest: preview && preview.digest,
      });
      const result = response.data;
      if (result && result.changed_since_preview) {
        // Nothing was written. Show the live preview in place of the one that was read.
        this.props.setAlert(result.message, "warning", 15000);
        this.setState({
          preview: { status: "success", plan: result.plan, digest: result.digest },
          typed: "",
          busy: false,
          changedSince: true,
        });
        return;
      }
      this.props.setAlert(...changeOutcome(result));
    } catch (err) {
      console.error(err);
      this.props.setAlert(...changeOutcomeUnknown(action, err));
    }
    this.close();
  };

  renderPreview() {
    const { action, preview, typed, busy, changedSince } = this.state;
    const plan = preview && preview.plan;
    if (!preview) return <LoadingCircle />;
    if (!plan || !plan.company) {
      return <div className="alert alert-warning">{preview.message || "That could not be previewed."}</div>;
    }
    if (plan.blockers.length) {
      return (
        <div className="alert alert-warning">
          <b>This can't be done:</b>
          <ul style={{ margin: 0 }}>
            {plan.blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      );
    }

    const users = new Set(plan.records.flatMap((r) => r.users.map((u) => u.id))).size;
    const jobs = plan.records.reduce((sum, r) => sum + r.shopify_jobs_expected, 0);
    const needsName = action !== "resume";
    const ready = !busy && (!needsName || typedNameMatches(typed, this.props.companyName));
    const label = `${TITLE[action]} all ${plan.records.length} product${plural(plan.records.length)}`;

    return (
      <div>
        {changedSince ? (
          <div className="alert alert-warning" style={{ marginBottom: ".5rem" }}>
            <b>This changed while you were reading it.</b> Nothing was written — below is the live preview.
            Confirm again if it is still what you want.
          </div>
        ) : null}
        <p style={{ marginBottom: ".25rem" }}>
          <b>{label}</b> at <b>{this.props.companyName}</b> — {users} user{plural(users)}{" "}
          {action === "resume" ? "get it back" : "lose access"}. <i style={{ color: "grey" }}>(live preview)</i>
        </p>
        <ul style={{ marginBottom: ".25rem" }}>
          {plan.records.map((r) => (
            <li key={r.access_id}>
              <b>{r.product.name || r.product.id}</b> — {r.users.length} user{plural(r.users.length)}
              {r.expired ? <i style={{ color: "darkred" }}> · expired in UMA, never revoked</i> : null}
              {action === "revoke" && r.is_paused ? <i> · paused</i> : null}
              {r.rls ? <i> · RLS rows are updated too</i> : null}
            </li>
          ))}
        </ul>
        {plan.skipped_expired && plan.skipped_expired.length ? (
          <p style={{ color: "darkred", margin: 0, fontSize: "13px" }}>
            Not included — expiration date passed; grant these again with a new date:{" "}
            {plan.skipped_expired.map((r) => r.product_name || r.access_id).join(", ")}
          </p>
        ) : null}
        {plan.warnings.map((w, i) => (
          <p key={i} style={{ color: "darkorange", margin: 0, fontSize: "13px" }}>
            {w}
          </p>
        ))}
        <p style={{ color: "grey", fontSize: "13px", marginBottom: ".5rem" }}>
          {jobs} Shopify tag job{plural(jobs)} will be queued on the 1-minute runner — not live when this returns.
          {action === "revoke"
            ? " A revoke also clears each User's history, so resume can't undo it — it would have to be granted again."
            : ""}
        </p>
        {needsName ? (
          <CustomInput
            name={"bulk_confirm_name"}
            label={`Type ${this.props.companyName} to confirm`}
            onChange={(e) => this.setState({ typed: e.target.value })}
            value={typed}
            width={"100%"}
          />
        ) : null}
        <span style={{ display: "flex", marginTop: ".5rem" }}>
          <button
            className={`btn btn-${action === "resume" ? "primary" : "danger"} btn-sm`}
            disabled={!ready}
            onClick={this.confirm}
          >
            {busy ? "Working…" : `${label} now`}
          </button>
        </span>
      </div>
    );
  }

  render() {
    const { action, busy } = this.state;
    return (
      <div style={{ marginTop: ".5rem" }}>
        <span style={{ display: "flex", flexDirection: "row" }}>
          {["pause", "resume", "revoke"].map((verb) => (
            <button
              key={verb}
              className={`btn btn-${action === verb ? "secondary" : "light"} btn-sm`}
              style={{ marginRight: ".375rem" }}
              disabled={busy}
              onClick={() => (action === verb ? this.close() : this.open(verb))}
              title={`${TITLE[verb]} every product this company holds — previewed first`}
            >
              {action === verb ? "Cancel" : `${TITLE[verb]} all`}
            </button>
          ))}
        </span>
        {action ? (
          <div className="listing" style={{ display: "block", marginTop: ".5rem" }}>
            {this.renderPreview()}
          </div>
        ) : null}
      </div>
    );
  }
}

const plural = (n) => (n === 1 ? "" : "s");

export default BulkAccessChange;
